<template lang="pug">
div.comment-more-like-this
  ErrorAlert(
    v-if="error"
    :stack="error.stack"
    :message="i18n.t('TheComment.FailedToLoadSimilarComments')"
    @retry="query.refetch"
    @report="() => report(error)"
  )

  VSkeletonLoader(
    v-if="query.isPending.value"
    type="article"
  )
  p(v-else-if="mltDocs.length === 0") {{ i18n.t("Global.None") }}
  div(v-else)
    CommentTeaserCard.my-2(
      v-for="(comment, key) in mltDocs"
      :key="key"
      :heading="comment.heading"
      :teaser="comment.teaser"
      :ranges="comment.ranges"
      :detail-level-name="comment.detailLevel.name[locale] ?? '?'"
      :detail-level-num-dots="numDotsPerDetailLevelWeight[comment.detailLevel.weight] ?? 0"
      :nid="comment.nid"
      :local-tid="localTid"
      :commentary-type-machine-name="comment.commentaryType.machineName"
      :show-default-reading-list="true"
      :show-reading-list-menu="true"
      :language="comment.language"
    )
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { computed } from "vue";
import { createVidRange, type VidRangeData } from "~/units/scripture";
import { useQuery } from "@tanstack/vue-query";
import { injectRequired } from "~/injectRequired";
import { LogFunctionKey, SolrClientKey } from "~/injectionKeys";
import {
  getDetailLevelFromWeightWithFallback,
  numDotsPerDetailLevelWeight,
} from "~/static/taxonomy/detailLevels";
import { getCommentaryTypeOrFallback } from "~/static/taxonomy/commentaryTypes";
import { makeResultAsyncSerializable } from "~/errors/util";
import { parseJsonItemsAndSkipFailures } from "~/units/json";
import { useReport } from "~/logging/report";

const report = useReport();

const i18n = useI18n();
const locale = i18n.locale;
const solrClient = injectRequired(SolrClientKey);
const log = injectRequired(LogFunctionKey);

interface Props {
  /**
   * The ID of the user's preferred local translation.
   */
  localTid: string;

  /**
   * The ID of the comment for which a list of similar content will be shown.
   */
  nid: number;
}

const props = defineProps<Props>();

const query = useQuery({
  queryKey: ["comment-more-like-this", computed(() => props.nid)],
  queryFn: () =>
    makeResultAsyncSerializable(
      () =>
        solrClient.queryCommentMoreLikeThis(
          props.nid,
          [locale.value],
          [
            "sm_topics_name",
            "ss_heading",
            "tm_X3b_en_body_full",
            "tm_X3b_en_body_summary",
          ],
          [
            "index_id",
            "its_nid",
            "bs_status",
            "ss_heading",
            "zs_body_teaser",
            "zm_scripture",
            "its_detail_weight",
            "sm_type_machine_name",
            "ss_search_api_language",
          ],
        ),
      log,
    ),
});

const mltDocs = computed(() => {
  return (query.data.value?.value ?? [])
    .filter((doc) => doc.bs_status && doc.index_id === "commentary_comments")
    .map((doc) => {
      return {
        nid: doc.its_nid,
        heading: doc.ss_heading,
        teaser: doc.zs_body_teaser,
        ranges: parseJsonItemsAndSkipFailures<VidRangeData>(
          doc.zm_scripture ?? [],
        ).map(createVidRange),
        detailLevel: getDetailLevelFromWeightWithFallback(
          doc.its_detail_weight ?? 0,
        ),
        commentaryType: getCommentaryTypeOrFallback(
          doc.sm_type_machine_name?.[0] || "",
        ),
        language: doc.ss_search_api_language,
      };
    });
});

const error = computed(() => query.error.value ?? query.data.value?.error);

onServerPrefetch(async () => {
  await query.suspense();
});
</script>
